import { orderBy } from 'lodash';
import { useMemo } from 'react';

import { useDeviceTrackHistory } from '@/api/device/getDeviceTrackHistory';
import { TrackItem } from '@/api/device/types';
import { isTrackSample, useOceansWaveData } from '@/api/oceansApi/useOceansWaveData';
import { CombinedQueryResult, pickQueryStatus } from '@/common/react-query';
import { useSpotterContext } from '@/context/SpotterContext';
import { DateRange } from '@/store';

export type UseDeviceTrackOptions = {
    config?: { enabled?: boolean };
    dateRange: DateRange | null;
};

/**
 * Primary entrypoint for SpotterContext-aware track (location history) data
 *
 * Will use either oceans-api/wave-data or fetch/track-history as appropriate. Both branches return the
 * samples newest first, so callers can treat `data[0]` as the most recent position without caring which
 * endpoint answered.
 */
export const useDeviceTrack = ({
    config: { enabled = true } = {},
    ...options
}: UseDeviceTrackOptions): CombinedQueryResult<TrackItem[]> => {
    const { spotterId, viaOceansApi } = useSpotterContext();

    const fetchApiResult = useDeviceTrackHistory({
        ...options,
        spotterId,
        config: { enabled: enabled && !viaOceansApi },
    });
    const oceansApiQuery = useOceansWaveData({
        ...options,
        spotterId,
        config: { enabled: enabled && viaOceansApi },
    });

    /*
     * One oceans-api request answers with the waves and the track together, and this is the track half.
     *
     * oceans-api answers oldest first. Sorted on the samples alone rather than on the query result, so the
     * array keeps its identity between renders: it is handed straight to the map layers, which redraw when
     * it changes.
     */
    const oceansApiSamples = oceansApiQuery.data;
    const sortedOceansApiTrack = useMemo(
        () => (oceansApiSamples ? orderBy(oceansApiSamples.filter(isTrackSample), 'timestamp', 'desc') : undefined),
        [oceansApiSamples]
    );

    if (!viaOceansApi) {
        return fetchApiResult;
    }

    return {
        ...pickQueryStatus(oceansApiQuery),
        data: sortedOceansApiTrack,
    };
};
